import {
  AssignmentMode,
  IMPORT_ENTITY_TYPES,
  IMPORT_MAX_ROWS,
  ImportEntityType,
  ImportFileError,
  ImportMessageCode,
  ImportRow,
  ParsedImportFile,
} from "src/core/utils/import/types";

const HEADER_SIREN = "siren";
const HEADER_OWNER = "idowner";
const HEADER_TYPE = "entitytype";
const HEADER_ASSIGNMENT = "idassignement";

const isEntityType = (value: string): value is ImportEntityType =>
  (IMPORT_ENTITY_TYPES as readonly string[]).includes(value);

const emptyRow = (lineNumber: number, partial: Partial<ImportRow> = {}): ImportRow => ({
  lineNumber,
  siren: "",
  idOwner: null,
  entityType: "",
  assigneeIds: [],
  isValid: false,
  duplicate: false,
  status: "error",
  existant: null,
  creationAuto: null,
  creationFolder: null,
  callEReputation: null,
  callFollowFolder: null,
  messageCodes: [],
  messageDetail: null,
  processedOrder: null,
  customerFolderId: null,
  folderFileId: null,
  ...partial,
});

/**
 * Découpe une ligne CSV séparée par des points-virgules, en respectant les guillemets.
 */
export const parseCsvLine = (line: string): string[] => {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];

    if (inQuotes) {
      if (char === '"') {
        if (line[index + 1] === '"') {
          current += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      continue;
    }

    if (char === ";") {
      cells.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  cells.push(current.trim());
  return cells;
};

const normalizeHeader = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/^\uFEFF/, "");

const headerError = (fileError: ImportFileError): ParsedImportFile => ({
  fileError,
  rows: [],
});

const validateRow = (
  cells: string[],
  columns: { siren: number; owner: number; type: number; assignment: number },
  lineNumber: number
): ImportRow => {
  const siren = (cells[columns.siren] ?? "").trim();
  const ownerRaw = (cells[columns.owner] ?? "").trim();
  const entityType = (cells[columns.type] ?? "").trim().toLowerCase();
  const assignmentRaw = columns.assignment >= 0 ? (cells[columns.assignment] ?? "").trim() : "";

  const messageCodes: ImportMessageCode[] = [];

  if (!/^\d{9}$/.test(siren)) {
    messageCodes.push("siren_invalid");
  }

  if (ownerRaw.length > 50) {
    messageCodes.push("id_owner_too_long");
  }

  if (!isEntityType(entityType)) {
    messageCodes.push("entity_type_invalid");
  }

  const assigneeIds = assignmentRaw
    .split(",")
    .map((id) => id.trim())
    .filter((id) => id.length > 0);

  const isValid = messageCodes.length === 0;

  return emptyRow(lineNumber, {
    siren,
    idOwner: ownerRaw.length > 0 ? ownerRaw : null,
    entityType,
    assigneeIds,
    isValid,
    status: isValid ? "pending" : "error",
    messageCodes,
  });
};

export const parseImportCsv = (content: string, mode: AssignmentMode): ParsedImportFile => {
  const withoutBom = content.replace(/^\uFEFF/, "");
  const lines = withoutBom.split(/\r?\n/);
  const filled = lines
    .map((line, index) => ({ line, lineNumber: index + 1 }))
    .filter(({ line }) => line.trim().length > 0);

  if (filled.length === 0) {
    return headerError("empty");
  }

  const headerCells = parseCsvLine(filled[0].line).map(normalizeHeader);
  const sirenIndex = headerCells.indexOf(HEADER_SIREN);
  const ownerIndex = headerCells.indexOf(HEADER_OWNER);
  const typeIndex = headerCells.indexOf(HEADER_TYPE);
  const assignmentIndex = headerCells.indexOf(HEADER_ASSIGNMENT);

  if (sirenIndex < 0 || ownerIndex < 0 || typeIndex < 0) {
    return headerError("missing_columns");
  }

  if (mode === "with" && assignmentIndex < 0) {
    return headerError("missing_assignment");
  }

  if (mode === "without" && assignmentIndex >= 0) {
    return headerError("unexpected_assignment");
  }

  if (filled.length - 1 > IMPORT_MAX_ROWS) {
    return headerError("too_many_rows");
  }

  const columns = {
    siren: sirenIndex,
    owner: ownerIndex,
    type: typeIndex,
    assignment: assignmentIndex,
  };

  const rows = filled
    .slice(1)
    .map(({ line, lineNumber }) => {
      const cells = parseCsvLine(line);
      const isBlank = cells.every((cell) => cell.length === 0);
      if (isBlank) return null;
      return validateRow(cells, columns, lineNumber);
    })
    .filter((row): row is ImportRow => row !== null);

  const seen = new Set<string>();
  rows.forEach((row) => {
    if (!row.isValid || row.siren.length === 0) return;
    if (seen.has(row.siren)) {
      row.duplicate = true;
      row.isValid = false;
      row.status = "error";
      row.messageCodes = [...row.messageCodes, "duplicate_siren"];
      return;
    }
    seen.add(row.siren);
  });

  return { fileError: null, rows };
};

export const templateCsv = (mode: AssignmentMode) =>
  mode === "with" ? "Siren;IdOwner;entityType;IdAssignement\n" : "Siren;IdOwner;entityType\n";
